import React, { useState, useRef, useEffect, useContext } from "react";
import { 
    Box, Button, Typography, Paper, CircularProgress, 
    IconButton, Divider, Grid, TextField, InputAdornment 
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faArrowLeft, faCheckCircle, faTimesCircle, 
    faTicketAlt, faReceipt 
} from "@fortawesome/free-solid-svg-icons";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Html5Qrcode } from "html5-qrcode";
import UserContext from "../../../navigation/context/userContext";
import { completeRedemption, validateRedemptionCode } from "../../../controllers/company/loyalty/RedemptionController";



export default function RedemptionScannerView() {
    const history = useHistory();
    const { userData } = useContext(UserContext);
    
    // Estados principales
    const [scanState, setScanState] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [scannedData, setScannedData] = useState(null);
    const [scannedCode, setScannedCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [billedAmount, setBilledAmount] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const html5QrCode = useRef(null);

    // Resolución dinámica del UUID del comercio
    const getActiveMerchantUuid = () => {
        return localStorage.getItem('work_mode_merchant_id') || userData?.entity?.uuid || null;
    };


    useEffect(() => {
        // 1. SOLO iniciamos si el estado es 'idle' (la pantalla está vacía y lista)
        if (scanState !== 'idle') return;

        let html5QrCode = null;

        // 2. Delay para asegurar que el DOM ya pintó el <Box id="reader-container">
        const timer = setTimeout(() => {
            html5QrCode = new Html5Qrcode("reader-container");
            
            html5QrCode.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 250, height: 250 } },
                (decodedText) => {
                    // DETECCIÓN EXITOSA
                    // Paramos la cámara antes de cambiar el estado de React
                    html5QrCode.stop().then(() => {
                        processCode(decodedText);
                    }).catch(err => console.error("Error al detener cámara:", err));
                },
                (err) => {}
            ).catch(err => {
                console.error("No se pudo iniciar la cámara:", err);
                // Si falla, manejamos el error visualmente
                setErrorMessage("No se pudo acceder a la cámara.");
                setScanState('error');
            });
        }, 500); // Espera medio segundo a que la UI se estabilice

        // 3. Limpieza: si el componente se desmonta o cambia el estado, apagamos todo
        return () => {
            clearTimeout(timer);
            if (html5QrCode && html5QrCode.isScanning) {
                html5QrCode.stop().catch(() => {});
            }
        };
    }, [scanState]);


    const processCode = async (code) => {
        setScannedCode(code);
        setScanState('loading');
        
        try {
            const merchantUuid = localStorage.getItem('work_mode_merchant_id') || userData?.entity?.uuid;
            const response = await validateRedemptionCode(code, merchantUuid);
            
            // Ahora la respuesta siempre viene con success: true (o manejamos el caso de error)
            setScannedData(response.data);
            setScanState('success');
        } catch (error) {
            setErrorMessage("Error al conectar con el servidor.");
            setScanState('error');
        }
    };

    const handleCompleteRedemption = async () => {
        setIsSubmitting(true);
        try {
            const merchantUuid = getActiveMerchantUuid();
            await completeRedemption(scannedCode, merchantUuid, billedAmount);
            resetScanner();
        } catch (error) {
            setErrorMessage("No se pudo completar el canje.");
            setScanState('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('es-ES', options);
    };

    const resetScanner = () => {
        setScanState('idle');
        setScannedData(null);
        setScannedCode('');
        setBilledAmount('');
        setErrorMessage('');
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton onClick={() => history.goBack()} sx={{ mr: 2, bgcolor: '#f1f5f9' }}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: '800' }}>Validar Cupón</Typography>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Paper elevation={0} sx={{ width: '100%', maxWidth: 450, p: 4, borderRadius: 5, border: '1px solid #eaeaea', textAlign: 'center' }}>
                    
                    {/* ESCÁNER */}
                    {scanState === 'idle' && (
                        <Box>
                            <Typography sx={{ mb: 2, fontWeight: 'bold' }}>Apunta al código QR</Typography>
                            <Box 
                                id="reader-container" 
                                sx={{ 
                                    width: '100%', 
                                    minHeight: '300px', // Altura mínima fija
                                    borderRadius: 2, 
                                    overflow: 'hidden',
                                    bgcolor: '#000',
                                    '& video': {
                                        width: '100% !important',
                                        height: '100% !important',
                                        objectFit: 'cover' // IMPORTANTE: Esto evita que se estire
                                    }
                                }} 
                            />
                        </Box>
                    )}

                    {/* LOADING */}
                    {scanState === 'loading' && <CircularProgress sx={{ my: 6 }} />}

                    {/* ESTADO ÉXITO / PROCESADO (Mostrando la tarjeta siempre) */}
                    {scanState === 'success' && scannedData && (
                        <Box sx={{ animation: 'fadeIn 0.3s', maxWidth: 450, mx: 'auto' }}>
                            
                            {/* Alerta de NO VÁLIDO si el flag es false */}
                            {!scannedData.is_valid && (
                                <Paper sx={{ p: 2, mb: 3, bgcolor: '#fef2f2', border: '1px solid #fecaca', textAlign: 'center' }}>
                                    <Typography color="error" fontWeight="bold">⚠️ {scannedData.reason}</Typography>
                                </Paper>
                            )}

                            {/* TARJETA DETALLADA (Siempre se muestra) */}
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, mb: 4, textAlign: 'left' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>CLIENTE</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>{scannedData.customerName}</Typography>
                                
                                <Divider sx={{ mb: 2 }} />
                                
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>PROMOCIÓN</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 1 }}>{scannedData.promoTitle}</Typography>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Box><Typography variant="caption" color="textSecondary">Modalidad</Typography>
                                    <Typography variant="body2" fontWeight="bold">{scannedData.modality}</Typography></Box>
                                    <Box sx={{ textAlign: 'right' }}><Typography variant="caption" color="textSecondary">Beneficio</Typography>
                                    <Typography variant="body2" fontWeight="bold" color="#10b981">
                                        {scannedData.discountType === 'percentage' ? `${scannedData.discountValue}% OFF` : `$${scannedData.discountValue}`}
                                    </Typography></Box>
                                </Box>
                                
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>
                                    VENCIMIENTO
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 'bold' }}>
                                    {scannedData.validUntil}
                                </Typography>
                            </Paper>

                            {/* BOTÓN CONDICIONAL */}
                            {scannedData.is_valid ? (
                                <Box>
                                    <TextField 
                                        fullWidth label="Monto facturado (ROI)" type="number" sx={{ mb: 3 }}
                                        onChange={(e) => setBilledAmount(e.target.value)} 
                                    />
                                    <Button fullWidth variant="contained" onClick={handleCompleteRedemption} sx={{ py: 1.5, borderRadius: 3 }}>
                                        Confirmar Canje
                                    </Button>
                                </Box>
                            ) : (
                                <Button fullWidth variant="contained" onClick={resetScanner} sx={{ py: 1.5, borderRadius: 3, bgcolor: '#64748b' }}>
                                    Volver
                                </Button>
                            )}
                        </Box>
                    )}

                    {/* ERROR */}
                    {scanState === 'error' && (
                        <Box>
                            <FontAwesomeIcon icon={faTimesCircle} size="3x" color="#ef4444" style={{ marginBottom: 20 }} />
                            <Typography variant="h6" sx={{ color: '#ef4444' }}>Error</Typography>
                            <Typography sx={{ mb: 3 }}>{errorMessage}</Typography>
                            <Button fullWidth variant="contained" onClick={resetScanner}>Escanear de nuevo</Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
}